export enum ReferralCategory {
  Doctors = 'doctors',
  Paramedical = 'paramedical',
  Mental = 'mental',
  Hospitals = 'hospitals',
  Imaging = 'imaging',
  LabTests = 'labTests',
  Equipment = 'equipment',
}

export enum ReferralStep {
  Lobby = 'lobby',
  CategoryList = 'categoryList',
  HospitalSelect = 'hospitalSelect',
  DepartmentSelect = 'departmentSelect',
  MentalInstituteSelect = 'mentalInstituteSelect',
}

export enum Region {
  All = 0,
  North = 1,
  Central = 2,
  South = 3,
}

export enum ServiceLocationType {
  Institute = 'institute',
  Home = 'home',
}

export interface IReferralItem {
  id: number;
  name: string;
  icon?: string;
  subject?: string;
  subjectId?: number;
  medicalSectorName?: string;
  departmentName?: string | null;
  existInHospital?: boolean | null;
  existInPsychologicHospital?: boolean | null;
  paramsTableTableKey?: string | null;
  paramsTableValue?: number | null;
  makatPrivate?: number | null;
  makatHospital?: number | null;
  cardNum?: number | null;
}

export interface IHospital {
  id: number;
  name: string;
  shikumSupNumber: number | string;
  sapSupNumber?: number | string;
  address?: string;
  phone?: string;
  isPsychiatric: boolean;
  region?: Region | string;
}

export type MentalTreatmentRecipient = 'self' | 'family';

export type MentalFamilyRelation = 'spouse' | 'child' | 'parent' | 'sibling';

export interface IMentalFamilyExistingCommitment {
  selfReferralId: number;
  validFrom: string;
  validTo: string;
  instituteName: string;
  phone?: string | null;
}

export interface IMentalFamilyMember {
  idNumber: string;
  fullName: string;
  relation: MentalFamilyRelation;
  existingCommitment?: IMentalFamilyExistingCommitment | null;
}

export interface IMentalActiveCommitmentDto {
  instituteName: string;
  validFrom: string;
  validTo: string;
  phone?: string | null;
  selfReferralId: number;
}

export interface IMentalFamilyMemberDto {
  relation: number;
  relationDesc: string;
  idNumber: string;
  firstName: string;
  lastName: string;
  gender: number;
  activeCommitment?: IMentalActiveCommitmentDto | null;
}

export interface IMentalFamilyTreatmentDto extends IReferralItem {
  members: IMentalFamilyMemberDto[];
}

export interface IMentalInstituteDto {
  name: string;
  phone?: string | null;
  supplierCode: string;
}

export interface IMentalHealthDto {
  selfTreatments: IReferralItem[];
  familyMembers?: IMentalFamilyTreatmentDto | null;
  institutes?: IMentalInstituteDto[];
}

export interface IRecentMentalTherapist {
  id: number;
  name: string;
  treatmentType: string;
  paramsTableTableKey?: string | null;
  paramsTableValue?: number | null;
  supplierId?: string | null;
}

export interface ISelfServiceReferralsData {
  doctors: IReferralItem[];
  paramedics: IReferralItem[];
  mentalHealth: IReferralItem[];
  recentMentalTherapists?: IRecentMentalTherapist[];
  mentalFamilyMembers?: IMentalFamilyMember[];
  mentalFamilyTreatment?: IReferralItem;
  mentalInstitutes?: IMentalInstitute[];
  hospitals: IHospital[];
  imaging: IReferralItem[];
  labTests: IReferralItem[];
  equipment: IReferralItem[];
}

export const REFERRAL_CATEGORY_DATA_KEY: Record<
  ReferralCategory,
  keyof ISelfServiceReferralsData
> = {
  [ReferralCategory.Doctors]: 'doctors',
  [ReferralCategory.Paramedical]: 'paramedics',
  [ReferralCategory.Mental]: 'mentalHealth',
  [ReferralCategory.Hospitals]: 'hospitals',
  [ReferralCategory.Imaging]: 'imaging',
  [ReferralCategory.LabTests]: 'labTests',
  [ReferralCategory.Equipment]: 'equipment',
};

export interface ISelfServiceReferralsResponse extends Omit<
  ISelfServiceReferralsData,
  | 'mentalHealth'
  | 'mentalFamilyMembers'
  | 'mentalInstitutes'
  | 'mentalFamilyTreatment'
> {
  mentalHealth: IMentalHealthDto;
}

export interface ICreateReferralPayload {
  supplierId: string | null;
  paramTableTableKey: string | null;
  paramsTableValue: number | null;
  hospitalId: number | null;
  medicalSectorId?: number | null;
  validFromDate: string;
  validToDate: string;
  /** Optional; digits only, max 9. Used for family mental-health referrals. */
  familyMemberIsraeliId?: string | null;
}

/** Same shape as GET api/SelfServiceReferrals/eligibility (SelfReferralEligibilityResultDto). */
export interface ISelfReferralEligibilityResult {
  isSuccess: boolean;
  numOfReferrals: number | null;
  monthOfLimit: number | null;
  errorId: number | null;
  selfReferralId: number;
}

export interface ICreateReferralResult {
  isSuccess: boolean;
  requestId?: string | null;
  errorId?: number | null;
  errorMessageKey?: string | null;
  numOfReferrals?: number | null;
  monthOfLimit?: number | null;
}

export const SelfReferralPingStatus = {
  Success: 1,
  Pending: 2,
  Timeout: 3,
  NotFound: 4,
} as const;

export type SelfReferralPingStatus =
  (typeof SelfReferralPingStatus)[keyof typeof SelfReferralPingStatus];

export interface ISelfReferralPingItem {
  ticker: string;
  status: SelfReferralPingStatus;
  fileContentBase64?: string | null;
}

export interface ISelfReferralFileResponse {
  contentType: string;
  fileContentBase64: string;
}

export interface IMentalInstitute {
  supplierCode: string;
  name: string;
  phone?: string | null;
}

export type ReferralSelectionType =
  | 'hospital'
  | 'privateClinic'
  | 'institute'
  | 'privateTherapist';

export interface IReferralSelection {
  category: ReferralCategory;
  item?: IReferralItem;
  hospital?: IHospital;
  department?: IReferralItem;
  institute?: IMentalInstitute;
  type?: ReferralSelectionType;
  treatmentRecipient?: MentalTreatmentRecipient;
  familyMember?: IMentalFamilyMember;
  recentTherapist?: IRecentMentalTherapist;
}

export interface IReferralCreatorParams {
  institutionName: string;
  departmentName?: string;
  category: ReferralCategory;
  itemId: number;
  hospitalId?: number;
  departmentId?: number;
  medicalSectorId?: number;
  supplierId?: string | null;
  paramTableTableKey?: string | null;
  paramsTableValue?: number | null;
  institutionPhone?: string | null;
  subject?: string;
  isFamilyMental?: boolean;
  familyMemberIsraeliId?: string | null;
  recipientName?: string;
  contactDepartmentUrl?: string;
}

export interface IReferralCreatorProps extends IReferralCreatorParams {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onFailure: () => void;
}
